/**
 * Project Properties Section
 *
 * Thin wrapper around ScopeEditorSection for project-property attributes.
 */

import React, { useMemo } from 'react';
import ScopeEditorSection from './ScopeEditorSection';
import { useRevitParameterOptions } from '../../../../shared/hooks/useRevitParameterOptions';
import {
  useMenuConfiguration,
  useConnectorBindingConfiguration,
} from '../../../../shared/stores/useConfigurationStore';
import {
  DEFAULT_PROJECT_PROPERTIES_PCF_ATTRIBUTE_NAMES,
  PROJECT_PROPERTIES_FIELD_BY_EDITOR_ROW_ID,
  PROJECT_PROPERTIES_EDITOR_ROW_ORDER,
} from '../../../../shared/config/mappingScopeEditorFieldDefaults';
import { buildScopeEditorAttributes } from '../../lib/buildScopeEditorAttributes';
import {
  buildPcfAttributeOptions,
  buildPreviousPagesOptions,
} from '../../lib/buildScopeEditorOptions';
import type { DropdownOption } from '../../../../shared/ui/forms/SearchableDropdown';
import type {
  ScopeEditorPersistenceState,
  ScopeEditorPersistenceStateUpdater,
} from '../../lib/scopeEditorPersistence';

export { DEFAULT_PROJECT_PROPERTIES_PCF_ATTRIBUTE_NAMES };

interface ProjectPropertiesSectionProps {
  enabled: boolean;
  projectPropertiesValue: { value: string };
  projectIdentifierValue: { value: string };
  projectNameValue: { value: string };
  areaValue: { value: string };
  attribute1ClientName: { value: string };
  attribute2ProjectAddress: { value: string };
  attribute3ClientDepartment: { value: string };
  attribute4ClientProjectNumber: { value: string };
  attribute5ClientDocumentNumber: { value: string };
  attribute6ClientCode: { value: string };
  attribute7EngPartyOffice: { value: string };
  attribute8EngPartyOfficeLocation: { value: string };
  attribute9EngPartyProjectNumber: { value: string };
  attribute10EngPartyAreaCode: { value: string };
  onProjectPropertiesValueChange: (value: string) => void;
  onProjectIdentifierValueChange: (value: string) => void;
  onProjectNameValueChange: (value: string) => void;
  onAreaValueChange: (value: string) => void;
  onAttribute1ClientNameChange: (value: string) => void;
  onAttribute2ProjectAddressChange: (value: string) => void;
  onAttribute3ClientDepartmentChange: (value: string) => void;
  onAttribute4ClientProjectNumberChange: (value: string) => void;
  onAttribute5ClientDocumentNumberChange: (value: string) => void;
  onAttribute6ClientCodeChange: (value: string) => void;
  onAttribute7EngPartyOfficeChange: (value: string) => void;
  onAttribute8EngPartyOfficeLocationChange: (value: string) => void;
  onAttribute9EngPartyProjectNumberChange: (value: string) => void;
  onAttribute10EngPartyAreaCodeChange: (value: string) => void;
  editorState?: ScopeEditorPersistenceState;
  onEditorStateChange?: (nextState: ScopeEditorPersistenceStateUpdater) => void;
}

const ProjectPropertiesSection: React.FC<ProjectPropertiesSectionProps> = (props) => {
  const { options: revitParamOptions } = useRevitParameterOptions({
    preferredParameterType: 'Instance',
  });
  const menuConfig = useMenuConfiguration();
  const connectorConfig = useConnectorBindingConfiguration();

  const previousPagesOptions = buildPreviousPagesOptions([
    { value: menuConfig.pipingSpecValue, label: 'PIPING-SPEC (Menu)', source: 'Menu settings' },
    {
      value: connectorConfig.materialValue?.value,
      label: 'MATERIAL (Connector)',
      source: 'Connector binding',
    },
  ]);

  const revitParameterOptions: DropdownOption[] = [...previousPagesOptions, ...revitParamOptions];

  const pcfAttributeOptions: DropdownOption[] = useMemo(
    () =>
      buildPcfAttributeOptions(
        PROJECT_PROPERTIES_EDITOR_ROW_ORDER,
        DEFAULT_PROJECT_PROPERTIES_PCF_ATTRIBUTE_NAMES,
        (name) => (name.startsWith('ATTRIBUTE') ? 'Project properties' : 'Project')
      ),
    []
  );

  const attributes = useMemo(
    () =>
      buildScopeEditorAttributes(
        PROJECT_PROPERTIES_EDITOR_ROW_ORDER,
        DEFAULT_PROJECT_PROPERTIES_PCF_ATTRIBUTE_NAMES,
        PROJECT_PROPERTIES_FIELD_BY_EDITOR_ROW_ID,
        props
      ),
    [props]
  );

  return (
    <ScopeEditorSection
      enabled={props.enabled}
      ariaLabel="Project property PCF attribute source editor"
      disabledMessage="Project property mappings belong to the setup scope. Enable Project properties in setup to map their PCF attributes."
      attributes={attributes}
      pcfAttributeOptions={pcfAttributeOptions}
      revitParameterOptions={revitParameterOptions}
      editorState={props.editorState}
      onEditorStateChange={props.onEditorStateChange}
    />
  );
};

export default ProjectPropertiesSection;
